import { Box, Typography } from '@mui/material';
import { ReactComponent as ChangeStateIcon } from 'assets/arrow-left-right.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/arrow-next.svg';
import { ReactComponent as DeleteIcon } from 'assets/delete-red.svg';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { MouseEvent, useReducer, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';

type DealSetting = {
  label: string;
  icon: JSX.Element;
  hasSubmenu: boolean;
  submenuOptions?: string[];
  submenuIcon?: JSX.Element;
};

const DEAL_SETTINGS_DATA: DealSetting[] = [
  {
    label: 'Change stage',
    icon: <ChangeStateIcon />,
    hasSubmenu: true,
    submenuOptions: ['Early', 'Develop', 'Active', 'Closing'],
    submenuIcon: <ArrowRightIcon />,
  },
  {
    label: 'View details',
    icon: <ViewIcon />,
    hasSubmenu: false,
  },
  {
    label: 'Edit deal',
    icon: <EditIcon />,
    hasSubmenu: false,
  },
  {
    label: 'Delete',
    icon: <DeleteIcon />,
    hasSubmenu: true,
  },
];

export const DealsSettings = ({
  anchorElement,
  onClose,
  isRow,
  startOperation,
  completeOperation,
  deal,
  onDealDeleted,
  onViewDetails,
  onEdit,
  onStageChange,
}: {
  anchorElement: SVGSVGElement | null;
  onClose: () => void;
  isRow?: boolean;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
  deal?: any;
  onDealDeleted?: () => void;
  onViewDetails?: () => void;
  onEdit?: () => void;
  onStageChange?: () => void;
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    rename: false,
    delete: false,
  });

  const onDeleteClick = async () => {
    if (!deal?.id && !deal?.dealId) {
      completeOperation('delete', 'Deal ID not found', true);
      return;
    }

    try {
      startOperation('delete', 'Deleting deal...');
      onClose();
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      const dealId = deal.id || deal.dealId;
      await DealsService.deleteDeal(dealId);
      
      // Refresh deals list
      if (onDealDeleted) {
        onDealDeleted();
      }

      completeOperation('delete', 'Deal deleted successfully');
    } catch (error: any) {
      completeOperation(
        'delete',
        error?.response?.data?.message || 'Failed to delete deal',
        true,
      );
    }
  };

  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  const handleStageClick = (e: MouseEvent) => {
    setSubmenuAnchor(e.currentTarget as HTMLElement);
    setDropdownVisible(true);
  };

  const handleMouseLeave = () => {
    setSubmenuAnchor(null);
    setDropdownVisible(false);
  };

  const handleStageChange = async (newStage: string) => {
    if (!deal?.id && !deal?.dealId) {
      completeOperation('update', 'Deal ID not found', true);
      return;
    }

    const dealId = deal.id || deal.dealId;
    const stageMap: Record<string, string> = {
      'Early': 'early',
      'Develop': 'develop',
      'Active': 'active',
      'Closing': 'closing',
    };

    const targetStage = stageMap[newStage] || newStage.toLowerCase();

    try {
      startOperation('update', 'Updating deal stage...');
      onClose();
      
      const payload = {
        dealStageName: targetStage,
      };
      
      await DealsService.updateDeal(dealId, payload);
      
      if (onStageChange) {
        onStageChange();
      }
      
      if (onDealDeleted) {
        onDealDeleted();
      }
      
      completeOperation('update', 'Deal stage updated successfully');
    } catch (error: any) {
      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update deal stage',
        true,
      );
    }
  };

  const handleViewDetails = () => {
    onClose();
    if (onViewDetails) {
      onViewDetails();
    }
  };

  const handleEdit = () => {
    onClose();
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <CustomPopOver
      onClick={(e: MouseEvent<SVGSVGElement>) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        {DEAL_SETTINGS_DATA.map((item: DealSetting) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
            }}
            key={item.label}
            component={'button'}
            onClick={
              item.label.includes('stage')
                ? handleStageClick
                : item.label === 'View details'
                  ? handleViewDetails
                  : item.label === 'Edit deal'
                    ? handleEdit
                    : item.label === 'Delete'
                      ? () => modalDispatch({ type: 'OPEN', payload: 'delete' })
                      : undefined
            }
          >
            {item.icon}
            <Typography
              variant='responsiveBody3'
              color={item.label === 'Delete' ? zeplinColor.Error : zeplinColor.BasicBlack}
            >
              {item.label}
            </Typography>
            {item.submenuIcon}
            {submenuAnchor && item.submenuOptions?.length && (
              <StageSubMenu
                options={item.submenuOptions}
                onClose={handleMouseLeave}
                anchorElement={submenuAnchor}
                onStageSelect={handleStageChange}
              />
            )}
          </Box>
        ))}
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={onDeleteClick}
        title='Delete Deal'
        description={`You’re about to delete the deal “${deal?.name || deal?.title || 'this deal'}” This action cannot be undone. Are you sure you want to delete?`}
      />
    </CustomPopOver>
  );
};

const StageSubMenu = ({
  options,
  onClose,
  anchorElement,
  onStageSelect,
}: {
  options: string[];
  onClose: () => void;
  anchorElement: HTMLElement;
  onStageSelect?: (stage: string) => void;
}) => {
  return (
    <CustomPopOver
      onClick={(e) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      vertical='right'
      horizontal='right'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        {options.map((option: string) => (
          <Box
            key={option}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
            }}
            onClick={() => {
              if (onStageSelect) {
                onStageSelect(option);
              }
              onClose();
            }}
            component={'button'}
          >
            <Typography variant='responsiveBody3'>{option}</Typography>
          </Box>
        ))}
      </Box>
    </CustomPopOver>
  );
};
